import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { isVideoFile, readVideoDurationMs } from "@/lib/parseVideoEmbed";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

/** Prefix relative `/media/files/...` (or other API-relative) paths with API_BASE_URL. */
export function resolveMediaUrl(url) {
  if (!url) return url;
  const s = String(url);
  if (/^https?:\/\//i.test(s)) return s;
  return `${API_BASE_URL}${s.startsWith("/") ? s : `/${s}`}`;
}

/**
 * True when a Social media URL must be fetched with Bearer auth
 * (auth content routes or Circa-hosted `/media/files/social/...` blobs).
 * External hosts (e.g. Giphy) return false.
 */
export function isSocialAuthMediaUrl(url) {
  if (!url) return false;
  const s = String(url);
  if (/giphy\.com/i.test(s)) return false;
  if (/\/social\/.+\/media\/.+\/content(?:\?|$)/i.test(s)) return true;
  if (s.startsWith("/media/files/social/")) return true;
  if (s.includes("/media/files/social/")) return true;
  return false;
}

/**
 * Authenticated blob fetch for Social media content paths.
 * Returns an object URL the caller must revoke.
 */
export async function fetchSocialMediaBlobUrl(accessToken, url) {
  if (!url || !accessToken) {
    throw new Error("Missing token or url");
  }
  const absolute = resolveMediaUrl(url);
  const res = await axios.get(absolute, {
    headers: authHeaders(accessToken),
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
}

/** MediaCarousel resolveSrc for Social posts (auth paths / Circa-hosted blobs). */
export function resolveSocialMediaSrc(accessToken) {
  return async (item) => {
    if (!item?.url) throw new Error("No media url");
    if (!isSocialAuthMediaUrl(item.url)) {
      return resolveMediaUrl(item.url);
    }
    return fetchSocialMediaBlobUrl(accessToken, item.url);
  };
}

/** Upload an image or video onto a social post (post must already exist). */
export async function uploadSocialPostMedia(accessToken, postId, file) {
  const form = new FormData();
  form.append("file", file);
  if (isVideoFile(file)) {
    try {
      const durationMs = await readVideoDurationMs(file);
      form.append("duration_ms", String(durationMs));
    } catch {
      // Let the server reject if duration is required and unavailable.
    }
  }
  const { data } = await axios.post(
    `${API_BASE_URL}/social/posts/${postId}/media`,
    form,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Org-plane media upload for official (authorOrgId) Social posts. */
export async function uploadOrgSocialPostMedia(
  accessToken,
  orgId,
  postId,
  file
) {
  const form = new FormData();
  form.append("file", file);
  if (isVideoFile(file)) {
    try {
      const durationMs = await readVideoDurationMs(file);
      form.append("duration_ms", String(durationMs));
    } catch {
      // server validates
    }
  }
  const { data } = await axios.post(
    `${API_BASE_URL}/org/${orgId}/social/posts/${postId}/media`,
    form,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Set (or clear when url is null/empty) a YouTube/Vimeo embed on a social post. */
export async function setSocialEmbed(accessToken, postId, url) {
  const { data } = await axios.post(
    `${API_BASE_URL}/social/posts/${postId}/embed`,
    { url: url || null },
    { headers: authHeaders(accessToken) }
  );
  return data;
}

export async function setOrgSocialEmbed(accessToken, orgId, postId, url) {
  const { data } = await axios.post(
    `${API_BASE_URL}/org/${orgId}/social/posts/${postId}/embed`,
    { url: url || null },
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Remove a media item by index from a social post. */
export async function removeSocialPostMedia(accessToken, postId, index) {
  const { data } = await axios.delete(
    `${API_BASE_URL}/social/posts/${postId}/media/${index}`,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Upload the single allowed image onto a social comment. */
export async function uploadSocialCommentMedia(accessToken, interactionId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(
    `${API_BASE_URL}/social/posts/interactions/${interactionId}/media`,
    form,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Attach a Giphy GIF to a social comment. */
export async function setSocialCommentGif(accessToken, interactionId, gif) {
  const { data } = await axios.post(
    `${API_BASE_URL}/social/posts/interactions/${interactionId}/gif`,
    gif,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Send a social DM containing a single image (optionally with text). */
export async function sendSocialDmImage(accessToken, conversationId, file, content) {
  const form = new FormData();
  form.append("file", file);
  if (content) form.append("content", content);
  const { data } = await axios.post(
    `${API_BASE_URL}/social/dm/conversations/${conversationId}/messages/media`,
    form,
    { headers: authHeaders(accessToken) }
  );
  return data;
}

/** Search Giphy for Social comments / DMs (proxied via API, no orgId required). */
export async function searchSocialGifs(accessToken, q, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/social/gifs/search`, {
    headers: authHeaders(accessToken),
    params: { q, limit: opts.limit },
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** Trending Giphy for Social comments / DMs (proxied via API, no orgId required). */
export async function trendingSocialGifs(accessToken, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/social/gifs/trending`, {
    headers: authHeaders(accessToken),
    params: { limit: opts.limit },
  });
  return Array.isArray(res.data) ? res.data : [];
}
